import { Box, Typography } from "@mui/material";
import { ErrorMessage, FormDropdownFilter, FormTextField } from "components";
import { AnimatePresence, motion } from "framer-motion";
import { Control } from "react-hook-form";
import { GetFormRules } from "lib/validation/formRules";
import { PartNumberMaxLength } from "lib/util/constants";
import * as labels from "./constants";

interface singlePartFieldsProps {
	/* eslint-disable-next-line @typescript-eslint/no-explicit-any */
	control: Control<any>;
	hasMultipleParts: boolean;
	dunsFilterType?: any;
	showDuns?: boolean;
	/** When false, Part Number is optional (not used while card is hidden by the FN gate). */
	partNumberRequired?: boolean;
}

export function SinglePartFields({
	control,
	hasMultipleParts,
	dunsFilterType,
	showDuns = false,
	partNumberRequired = true,
}: singlePartFieldsProps): React.JSX.Element {
	const partNumberRules = partNumberRequired
		? GetFormRules("partNumber")
		: {
				validate: (value: string) => {
					if (value == null || value === "") return true;
					const trimmed = String(value).trim();
					// Reject whitespace-only so optional fields cannot persist blank PN values.
					if (trimmed === "") {
						return "Part Number cannot be empty or contain only whitespace";
					}
					if (trimmed.length > PartNumberMaxLength) {
						return `Part Number must be ${PartNumberMaxLength} characters or fewer`;
					}
					return true;
				},
			};

	return (
		<AnimatePresence mode="sync">
			{!hasMultipleParts && (
				<Box
					component={motion.div}
					initial={{ opacity: 0, height: 0 }}
					animate={{ opacity: 1, height: "auto" }}
					exit={{ opacity: 0, height: 0 }}
					transition={{ duration: 0.3 }}
					style={{ overflow: "visible" }}
				>
					<Box display="flex" flexDirection="column" gap={2}>
						<Box data-test-selector="part-number-field">
							<Typography sx={{ fontWeight: 600, fontSize: "0.8rem", mb: 0.5 }}>
								{labels.PartNumber}
							</Typography>
							<FormTextField
								control={control}
								name="partNumber"
								rules={partNumberRules}
								renderError={(errMsg) => <ErrorMessage msg={errMsg} />}
							/>
						</Box>

						{showDuns && (
							<Box data-test-selector="duns-dropdown">
								<Typography sx={{ fontWeight: 600, fontSize: "0.8rem", mb: 0.5 }}>
									{labels.SupplierDuns}
								</Typography>
								<FormDropdownFilter
									control={control}
									name="duns"
									filterType={dunsFilterType}
									keyFunc={(supplierPlant: any) => supplierPlant.id}
									labelFunc={(supplierPlant: any) => supplierPlant.DUNName}
									rules={{ required: `${labels.SupplierDuns} is required` }}
									renderError={(err: any) =>
										err
											.IfErr((msg: string) => <ErrorMessage msg={msg} />)
											.UnwrapOrNil()
									}
								/>
							</Box>
						)}
					</Box>
				</Box>
			)}
		</AnimatePresence>
	);
}
